import { ReactElement, useMemo } from 'react';
import { Tooltip } from '@mui/material';

import { useAccessFeatureModalContext } from '~app/contexts/AccessFeatureModalContext';
import { useSubscriptionFeatures, useAppSelector } from '~hooks/index';
import { Nullable } from '~globals/types';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser } from '~utils/user';
import { formatterDate } from '~utils/formatter';

import { ACCESS_ALERT_TEXT } from '../utils';
import AccessFeatureHidden from '../Hidden';

import { AccessFeatureNextPlanChipProps } from './types';
import { AccessFeatureNextPlanChipRoot } from './styles';

const AccessFeatureNextPlanChip = ({
  validFeature,
  availableToDate,
  openAccessFeatureModalOnClick = false,
  variant = 'outlined',
  gutterLeft = true,
  gutterRight = true,
  ...restProps
}: AccessFeatureNextPlanChipProps): Nullable<ReactElement> => {
  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const {
    getNextPlanFeature,
    validateFeatureAccess,
    validateFeatureAvailableToDate,
  } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const isDemo = useMemo(
    () =>
      !validateFeatureAccess(validFeature) &&
      validateFeatureAvailableToDate(availableToDate),
    [
      availableToDate,
      validFeature,
      validateFeatureAccess,
      validateFeatureAvailableToDate,
    ],
  );

  const title = useMemo(() => {
    if (isDemo && availableToDate) {
      return `Función de prueba disponible hasta el ${formatterDate(
        availableToDate,
      )}. Disponible en planes superiores o de pago.`;
    }

    return ACCESS_ALERT_TEXT;
  }, [availableToDate, isDemo]);

  const label = useMemo(
    () => (isDemo ? 'DEMO' : getNextPlanFeature(validFeature)),
    [getNextPlanFeature, isDemo, validFeature],
  );

  const handlerClick = useMemo(() => {
    if (!isDemo && openAccessFeatureModalOnClick && isAdminUser(user)) {
      return () =>
        handleOpenAccesFeatureModal({
          validFeature,
          validateAvailableRoutesState: false,
        });
    }

    return undefined;
  }, [
    openAccessFeatureModalOnClick,
    user,
    handleOpenAccesFeatureModal,
    isDemo,
    validFeature,
  ]);

  return (
    <AccessFeatureHidden
      validFeature={validFeature}
      availableToDate={availableToDate}
      type="hasAccess"
    >
      <Tooltip title={title} arrow>
        <AccessFeatureNextPlanChipRoot
          label={label}
          variant={variant}
          color="error"
          onClick={handlerClick}
          gutterLeft={gutterLeft}
          gutterRight={gutterRight}
          {...restProps}
        />
      </Tooltip>
    </AccessFeatureHidden>
  );
};

export default AccessFeatureNextPlanChip;
